import { useMemo } from 'react';
import {
  AssignedQuestionnaireDto,
  AssignedQuestionnaireStatus,
  FetchAssignedQuestionnairesParams,
  QuestionnaireInconsistenciesResponse,
  QuestionnaireType,
  useAssignedQuestionnairesQuery,
  usePreopQuestionnaireInconsistenciesQuery
} from '@/modules/questionnaire';
import { useQueryForCurrentPatient } from '@/hooks';

export type QuestionnaireAccuracy =
  | 'high'
  | 'acceptable'
  | 'inconsistent'
  | 'poor';

export const usePreopQuestionnaireAccuracy = () => {
  const {
    currentData: preopQuestionnaires,
    isLoading: isPreopQuestionnairesLoading
  } = useQueryForCurrentPatient<
    AssignedQuestionnaireDto[],
    FetchAssignedQuestionnairesParams
  >({
    useQuery: useAssignedQuestionnairesQuery,
    queryArg: {
      questionnaireType: QuestionnaireType.PREOP
    }
  });

  const preopCompletedQuestionnaires = useMemo(
    () =>
      preopQuestionnaires?.filter(
        (q) => q.status === AssignedQuestionnaireStatus.COMPLETED
      ),
    [preopQuestionnaires]
  );

  const allNotStarted = useMemo(
    () =>
      !!preopQuestionnaires?.length &&
      preopQuestionnaires.every(
        (q) => q.status === AssignedQuestionnaireStatus.NOT_STARTED
      ),
    [preopQuestionnaires]
  );

  const { currentData: questionnaireInconsistencies } =
    useQueryForCurrentPatient<QuestionnaireInconsistenciesResponse>({
      useQuery: usePreopQuestionnaireInconsistenciesQuery,
      queryConfig: {
        skip: !preopCompletedQuestionnaires?.length
      }
    });

  const accuracy: QuestionnaireAccuracy | undefined = useMemo(() => {
    const penalty = questionnaireInconsistencies?.totalPenalty;
    if (
      typeof penalty !== 'number' ||
      !questionnaireInconsistencies?.questionnaireType
    ) {
      return undefined;
    }

    if (penalty <= 10) {
      return 'high';
    }

    if (penalty < 20) {
      return 'acceptable';
    }

    if (penalty <= 30) {
      return 'inconsistent';
    }

    return 'poor';
  }, [
    questionnaireInconsistencies?.questionnaireType,
    questionnaireInconsistencies?.totalPenalty
  ]);

  const preopQuestionnaireCompleted = !!preopCompletedQuestionnaires?.length;

  return {
    preopQuestionnaires,
    isPreopQuestionnairesLoading,
    preopQuestionnaireCompleted,
    allNotStarted,
    accuracy,
    questionnaireInconsistencies
  };
};
